import DOMUtils from "@utils/dom.utils";
import embedApi from "@apis/embed.api";
import appConfigs from "@configs/app";
import { isDefined, isEmpty } from "@utils/var.check";
import { URL_PREFIX } from "@modules/app-list/service/app.list.constant";
import { FrameAttributes } from "@plugins/richtext/types.ts";

export default {
  isEmbedded() {
    return window.location !== window.parent.location;
  },
  getIframes(input) {
    const div = document.createElement("div");
    div.innerHTML = input;
    return div.querySelectorAll("iframe");
  },
  createIframe(url) {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("src", url);
    iframe.setAttribute("height", String(480));
    iframe.setAttribute("width", String(680));
    return iframe;
  },
  getValidFrameElement(iframe, data, allowedNode) {
    const slsFrame = this.getEmbedCodeFrame(iframe, data, allowedNode);
    const container = DOMUtils.createElement("div", {
      class: "embed-website-wrapper",
    });
    const node = DOMUtils.createElement("div", { class: "embed-website" });
    node.append(slsFrame);
    container.append(node);
    return container;
  },
  getEmbedCodeFrame(iframe, data, allowedNode) {
    const slsFrame = this.convertIframeToDiv(iframe, data);
    this.cleanUpAttributes(slsFrame, allowedNode);
    DOMUtils.addClass(slsFrame, "sls-frame");
    DOMUtils.addClass(slsFrame, "embed-code");
    slsFrame.setAttribute("websiteuuid", data.websiteUuid);
    if (data.urlAppendix) {
      slsFrame.setAttribute("data-urlappendix", data.urlAppendix);
    }
    return slsFrame;
  },
  getValidFrameElementByUrl(data) {
    const slsFrame = this.getEmbedUrlFrame(data);
    const container = DOMUtils.createElement("div", {
      class: "embed-website-wrapper",
    });
    const node = DOMUtils.createElement("div", { class: "embed-website" });
    node.append(slsFrame);
    container.append(node);
    return container;
  },
  getEmbedUrlFrame(data) {
    const slsFrame = DOMUtils.createElement("sls-frame", {
      name: "embed_website",
      class: "sls-frame embed-url",
      scrolling: "yes",
      allowfullscreen: "allowfullscreen",
      websiteUuid: data.websiteUuid,
      width: data.width,
      height: data.height,
      src: data.urlCode,
    });
    if (data.urlAppendix) {
      slsFrame.setAttribute("data-urlappendix", data.urlAppendix);
    }
    return slsFrame;
  },
  getValidFrameElementByAppUrl(data) {
    const slsFrame = this.getEmbedAppFrame(data);
    const container = DOMUtils.createElement("div", {
      class: "embed-website-wrapper",
    });
    const node = DOMUtils.createElement("div", { class: "embed-website" });
    node.append(slsFrame);
    container.append(node);
    return container;
  },
  getEmbedAppFrame(data) {
    const frame = DOMUtils.createElement("sls-frame", {
      name: "embed_website",
      class: "sls-frame embed-app",
      scrolling: "yes",
      allowfullscreen: "allowfullscreen",
    });
    frame.setAttribute("id", Math.round(Date.now() / 1000));
    frame.setAttribute("appurl", data.appUrl ? data.appUrl : "");
    frame.setAttribute("custom", data.custom ? data.custom : "");
    frame.setAttribute("appuuid", data.uuid);
    return frame;
  },
  convertIframeToDiv(iframe, data) {
    if (iframe) {
      const clone = iframe.cloneNode(true);
      clone.setAttribute("name", "embed_website");
      // clone.setAttribute("src", clone.getAttribute("src").trim());
      clone.setAttribute("websiteUUid", data.websiteUuid);
      if (data.urlAppendix) {
        clone.setAttribute("data-urlappendix", data.urlAppendix);
      }
      let slsFrameString = clone.outerHTML;
      // NOTE: tinymce not accept <sls-frame> tag, use <div> instead
      slsFrameString = slsFrameString.replace(/<iframe/gi, "<sls-frame"); // start tag
      slsFrameString = slsFrameString.replace(/iframe>/gi, "sls-frame>"); // end tag
      const div = DOMUtils.createElement("div");
      div.innerHTML = slsFrameString;
      return div.firstChild;
    }
    return "";
  },
  convertSlsFrameToIframe(slsFrame, context) {
    if (slsFrame) {
      const clone = slsFrame.cloneNode(true);
      let iFrameString = clone.outerHTML;
      iFrameString = iFrameString.replace(/<div|<sls-frame/gi, "<iframe"); // new and old tag
      iFrameString = iFrameString.replace(/div>|sls-frame>/gi, "iframe>"); // new and old tag
      const div = DOMUtils.createElement("div");
      div.innerHTML = iFrameString;
      const iframe = div.firstChild;

      const id = slsFrame.getAttribute("id");
      const appUuid = slsFrame.getAttribute("appuuid");
      const appUrl = slsFrame.getAttribute("appurl");
      const custom = slsFrame.getAttribute("custom");
      if (appUuid) {
        const src = this.constructAppUrl(
          {
            id: id,
            appurl: appUrl,
            appuuid: appUuid,
            custom: custom,
          },
          context,
        );
        iframe.setAttribute("src", src);
      }
      return iframe;
    }
    return "";
  },
  convertIframeToSlsFrame(iframe, allowedNode) {
    if (iframe) {
      const appUuid = iframe.getAttribute("appuuid");
      if (appUuid) {
        // EMBED APP clean up
        const frame = DOMUtils.createElement("sls-frame", {
          name: "embed_website",
          class: "embed-app",
          scrolling: "yes",
          allowfullscreen: "allowfullscreen",
        });
        frame.setAttribute("id", iframe.getAttribute("id"));
        const appUrl = iframe.getAttribute("appurl");
        frame.setAttribute("appurl", appUrl ? appUrl : "");
        frame.setAttribute("appuuid", appUuid);
        return frame;
      } else {
        // EMBED CODE / EMBED URL clean up
        // clone it because we dont want to manipulate the dom directly
        const clone = iframe.cloneNode(true);
        let slsFrameString = clone.outerHTML;
        //add name attribute so that tinymce will treat it as nonEmptyElements
        clone.setAttribute("name", "embed_website");
        slsFrameString = slsFrameString.replace(/<iframe/gi, "<sls-frame"); // start tag
        slsFrameString = slsFrameString.replace(/iframe>/gi, "sls-frame>"); // end tag

        const div = DOMUtils.createElement("div");
        div.innerHTML = slsFrameString;
        this.cleanUpAttributes(div.firstChild, allowedNode);
        return div.firstChild;
      }
    }
    return "";
  },
  cleanUpAttributes(node, allowedNode) {
    const attrs = node.attributes;
    const allowedAttrsSettings = allowedNode[node.tagName.toLowerCase()];
    if (allowedAttrsSettings) {
      for (let i = attrs.length - 1; i >= 0; i--) {
        const attr = attrs[i];
        if (allowedAttrsSettings.length === 0 || !allowedAttrsSettings.includes(attr.name)) {
          node.removeAttribute(attr.name);
        }
      }
    }
  },
  prependProtocol(url) {
    //prepend protocol if not present
    url = url.includes(URL_PREFIX.HTTPS) ? url : URL_PREFIX.HTTPS + url;
    return url;
  },
  createHyperlink(urlCode) {
    const linkElement = DOMUtils.createElement("a");
    linkElement.setAttribute("data-mce-src", this.prependProtocol(urlCode));
    linkElement.target = "_blank";
    linkElement.href = this.prependProtocol(urlCode);
    linkElement.appendChild(document.createTextNode(urlCode));
    return linkElement;
  },
  getSanitizedSlsFrame: function (attributes) {
    // let clone = original.cloneNode(true);
    const slsFrame = DOMUtils.createElement("sls-frame", attributes);
    const dataAttrsToDelete: string[] = [];
    for (const key in attributes) {
      if (key.indexOf("data-mce") > -1 || key.indexOf("sandbox") > -1) {
        dataAttrsToDelete.push(key);
      }
    }

    dataAttrsToDelete.forEach((attrName) => {
      slsFrame.removeAttribute(attrName);
    });

    return slsFrame;
  },
  updateStyleAttribute(slsFrame) {
    const styleAttribute = slsFrame.getAttribute("style");
    if (styleAttribute) {
      slsFrame.removeAttribute("style");
      slsFrame.setAttribute("data-style", styleAttribute); // change style with data-style so the styling on sls frame won't apply during rendering
    }
  },
  getValidFrameAttributes(attribute) {
    const attrs = attribute;
    const frameAttributes: FrameAttributes = {};
    for (let i = attrs.length - 1; i >= 0; i--) {
      const attr = attrs[i];
      frameAttributes[attr.name] = attr.value;
    }
    return frameAttributes;
  },
  validateEmbedUrl(url) {
    if (!this.isValidUrl(url, 500)) return "embed_website_url_format_invalid";
    // if (!this.isAllowedUrl(url, allowedUrlPrefix)) return "embed_website_invalid";
    return "";
  },
  isValidUrl(url, limit) {
    if (!url) return false;
    // reject non string objects
    if (typeof url !== "string") return false;
    // reject insecure websites
    if (url.includes("http://")) return false;
    if (url.length > limit) return false;
    // accept relative
    try {
      return !!new URL(url);
    } catch {
      return false;
    }
  },
  isAllowedUrl(url, allowedUrlPrefix) {
    if (url) {
      let result = false;
      for (let i = 0, len = allowedUrlPrefix.length; i < len; i++) {
        if (url.toLowerCase().startsWith(allowedUrlPrefix[i])) {
          result = true; // is in whitelist, ok
          break;
        }
      }
      return result;
    }
    return false; // url is empty, not ok
  },
  isWhitelistedAppUrl(appMap, appUrl, appUuid) {
    //remove https protocol if it exists and strip parameters
    appUrl = appUrl.replace("https://", "");
    appUrl = appUrl.split(/[/?#]/)[0];
    //test if optional url is in whitelist domains
    const selectedApp = appMap[appUuid];
    if (isDefined(selectedApp)) {
      const selectedDomains = selectedApp.whitelistDomains.split(",");
      if (selectedDomains.includes(appUrl)) {
        return true;
      }
    }
    return false; // url is not in whitelist
  },
  isWhitelistedResourceUrl(app, appUrl) {
    //remove https protocol if it exists and strip parameters
    appUrl = appUrl.replace("https://", "");
    appUrl = appUrl.split(/[/?#]/)[0];
    //test if optional url is in whitelist domains
    if (isDefined(app)) {
      const selectedDomains = app.whitelistDomains.split(",");
      if (selectedDomains.includes(appUrl)) {
        return true;
      }
    }
    return false; // url is not in whitelist
  },
  validateEmbedCode(embedCode) {
    const iframes = this.getIframes(embedCode);
    if (iframes.length !== 1) return "embed_website_code_format_invalid"; // not ok, no iframe or more than 1 iframe
    const iframe = iframes[0]; // get exact iframe
    if (!this.isValidUrl(iframe.src, 500)) return "embed_website_url_format_invalid"; // not ok, url invalid
    // if (!this.isAllowedUrl(iframe.src, allowedUrlPrefix)) return "embed_website_invalid"; // not ok, url not in whitelist
    return "";
  },
  validateAppUrl(app, appUrl) {
    if (!this.isValidUrl(appUrl, 250)) return "embed_website_app_optional_url_format_invalid";
    if (!this.isWhitelistedResourceUrl(app, appUrl)) return "embed_website_invalid";
    return "";
  },
  validateAllowedUrl(url, allowedUrlPrefix) {
    if (!this.isAllowedUrl(url, allowedUrlPrefix)) return "embed_website_invalid"; // not ok, url not in whitelist
    return "";
  },
  constructAppUrl(data, context) {
    const resourceUuid = context.resourceUuid;
    let componentId;
    if (context.componentId) {
      componentId = context.componentId;
    } else if (context.pageId) {
      componentId = context.pageId;
    } else {
      componentId = "desc";
    }

    let assignmentUuid = "";
    if (context.assignmentUuid) {
      assignmentUuid = `_${context.assignmentUuid}`;
    }

    const resourceLinkId = `${resourceUuid}_${componentId}_${data.id}_${data.appuuid}${assignmentUuid}`;
    const customQueryParam = !isEmpty(data.custom) ? `&custom=${data.custom}` : ``;
    let requestParam = `contextId=${resourceUuid}&resourceLinkId=${resourceLinkId}${customQueryParam}`;

    if (data.appurl) {
      requestParam += "&appUrl=" + encodeURIComponent(data.appurl);
    }

    return `${appConfigs.vleDomain}/app/embed/${data.appuuid}?${requestParam}`;
  },
  loadWhiteList() {
    return new Promise((resolve, reject) => {
      const whiteList = sessionStorage.getItem("whiteList");
      if (whiteList) {
        resolve(JSON.parse(whiteList));
        return;
      } else {
        embedApi.fetchWhiteList().then(
          (response) => {
            try {
              // try-catch here as setItem will throw exception if storage is full
              sessionStorage.setItem("whiteList", JSON.stringify(response.data));
            } catch {
              // TODO: to check if need to remove and set again?
            }
            resolve(response.data);
            return;
          },
          () => {
            reject();
          },
        );
      }
    });
  },
};
